import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Popconfirm,
  message,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteTwoTone,
  BarsOutlined,
  BarcodeOutlined,
  EditTwoTone,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  getProductCategories,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
  sendCategoryToOdoo,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../context/AppContextProvider";
import DownloadImportTemplate from "../../../components/DownloadImportTemplate";
import ImportItems from "../../../components/ImportItems";

const ProductCategories = () => {
  const { appConfig, authUser } = useContext(AppContext);
  const ADD_CATEGORY = "Add Category";
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [productCategoriesTableData, setProductCategoriesTableData] = useState(
    []
  );
  const [modalTitle, setModalTitle] = useState(ADD_CATEGORY);
  const [addCategoryForm] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    setIsDataLoading(true);
    getProductCategories()
      .then((response) => {
        setProductCategoriesTableData(response.data.data.product_categories);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData]);

  const showModal = () => {
    setModalVisible(true);
    // Set Modal Title
    setModalTitle(t("category_add"));
  };

  const handleAPIResponse = (response, type) => {
    let data = response.data || response.response.data;
    if (data.success === true) {
      setReloadTableData(!reloadTableData);
      // Success
      if (type === ADD_CATEGORY) {
        NotificationWithIcon(
          "success",
          t("category_added"),
          t("category_added_successfully")
        );
      } else {
        NotificationWithIcon(
          "success",
          t("category_updated"),
          t("category_updated_successfully")
        );
      }
      // Reset Form Fields
      addCategoryForm.resetFields();
      // Hide Modal
      setModalVisible(false);
    } else {
      // Error

      let errorMessages = Object.values(data.errors);
      errorMessages.forEach(function (key, value) {
        let temp = Object.values(key);
        message.error(temp[0]);
      });
    }
  };

  const handleAddEdit = () => {
    addCategoryForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoading(true);
        // Sending Request to API
        if (modalTitle === ADD_CATEGORY || modalTitle === t("category_add")) {
          createProductCategory(values)
            .then((response) => handleAPIResponse(response, ADD_CATEGORY))
            .catch((info) => {
              console.log("API Failed:", info.response);
            })
            .finally(() => {
              // Set Loading False
              setLoading(false);
            });
        } else {
          updateProductCategory(values)
            .then((response) => handleAPIResponse(response, null))
            .catch((info) => {
              console.log("API Failed:", info.response);
            })
            .finally(() => {
              // Set Loading False
              setLoading(false);
            });
        }
      })
      .catch((info) => {
        console.log("API Failed:", info);
      });
  };

  const handleCancel = () => {
    // Reset Form Fields
    addCategoryForm.resetFields();
    setModalVisible(false);
  };

  const deleteCategory = (id) => {
    deleteProductCategory(id)
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("category_deleted"),
            t("category_deleted_successfully")
          );
        } else {
          // Error
          NotificationWithIcon(
            "error",
            t("something_went_wrong"),
            data.message
          );
        }
      })
      .catch((info) => {
        console.log("API Failed:", info.response);
      });
  };

  const handleEditModal = (id) => {
    // Find category from DataArray
    const category = productCategoriesTableData.find(
      (category) => category.id === id
    );

    let category_name;

    appConfig.direction === "ltr"
      ? (category_name = category.name)
      : (category_name = category.name_ar);

    // Set Modal Title
    setModalTitle(t("category_edit") + " : " + category_name);

    // Show Modal
    setModalVisible(true);

    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    addCategoryForm.resetFields();

    // Set Form Fields
    addCategoryForm.setFieldsValue({
      id: category.id,
      name: category.name,
      name_ar: category.name_ar,
    });
  };

  const handleSendToOdoo = (id) => {
    sendCategoryToOdoo(id)
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("send_to_odoo"),
            t("item_send_to_odoo_successfully")
          );
        } else {
          // Error
          NotificationWithIcon(
            "error",
            t("something_went_wrong"),
            data.message
          );
        }
      })
      .catch((info) => {
        console.log("API Failed:", info.response);
      });
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("menu_home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/products",
      breadcrumbName: t("menu_products"),
      icon: <BarcodeOutlined />,
    },
    {
      path: "categories",
      breadcrumbName: t("menu_categories"),
      icon: <BarsOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("col_category"),
      width: "70%",
      render: (record) => {
        return (
          <>
            {record.name_ar}
            <br />
            {record.name}
          </>
        );
      },
    },
  ];
  const odooStatusColumn = [
    {
      title: t("col_odoo_status"),
      align: "center",
      render: (record) => {
        return (
          <>
            {record.odoo_reference_id ? (
              <CheckOutlined style={{ fontSize: "22px", color: "#52c41a" }} />
            ) : (
              <>
                <CloseOutlined
                  style={{
                    fontSize: "22px",
                    color: "#ff0000",
                    display: "block",
                    paddingBottom: "5px",
                  }}
                />
                <Button
                  type="primary"
                  size="small"
                  style={{ marginLeft: "10px" }}
                  onClick={() => handleSendToOdoo(record.id)}
                >
                  {t("send_to_odoo")}
                </Button>
              </>
            )}
          </>
        );
      },
    },
  ];
  const actionPartColumn = [
    {
      title: t("col_action"),
      align: "center",
      render: (record) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => handleEditModal(record.id)}
              title={t("category_edit")}
            >
              <EditTwoTone twoToneColor="#1890ff" />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => deleteCategory(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <DeleteTwoTone
                twoToneColor="#ff0000"
                title={t("delete_entity")}
              />
            </Popconfirm>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: "20px" }}>
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          style={{ padding: "0" }}
          title={t("menu_categories")}
          breadcrumb={CustomBreadcrumb}
          ghost={false}
          extra={[
            <Button
              key="open-add-category-modal"
              type="primary"
              onClick={showModal}
            >
              + {t("category_add")}
            </Button>,
            <DownloadImportTemplate type="category" key="import-template" />,
            <ImportItems
              type="category"
              callbackLoading={setReloadTableData}
              key="import-items"
            />,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={
            authUser.external_integrations.length
              ? [
                  ...detailsPartColumns,
                  ...odooStatusColumn,
                  ...actionPartColumn,
                ]
              : [...detailsPartColumns, ...actionPartColumn]
          }
          dataSource={productCategoriesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        footer={[
          <Button key="back" loading={loading} onClick={handleCancel}>
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleAddEdit}
          >
            {modalTitle === ADD_CATEGORY || modalTitle === t("category_add")
              ? t("category_add")
              : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-category-form"
          layout="vertical"
          onFinish={handleAddEdit}
          style={{ width: "100%", margin: "0" }}
          form={addCategoryForm}
        >
          <Form.Item
            name="name"
            label={t("name_en")}
            rules={[
              {
                required: true,
                message: t("category_name_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name_ar"
            label={t("name_ar")}
            rules={[
              {
                required: true,
                message: t("category_name_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item name="id" style={{ display: "none" }}>
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProductCategories;
